import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';

import {
  getDefaultParams,
  getActiveParams
} from '../../../../../utils/urlHelpers/engines';
import {
  getRegions,
  getSubdivisions,
  getCities
} from '../../../../../utils/multiFacetHelper';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleHeader
} from '../../../../../components/Collapsible';
import MainContent from '../../MainContent';
import PillBox from '../../../../../components/PillBox';
import SearchAlertsContainer from '../../../components/SearchAlerts/components/Engines';
import {
  FilterForSaleBy,
  FilterYear,
  FilterYearForm,
  FilterRegion,
  FilterSubdivision,
  FilterCountry,
  FilterCity
} from '../../../components/Filter';
import {
  FilterCategory,
  FilterFuelType,
  FilterHorsePower,
  FilterMake,
  FilterModels
} from '../Filter';
import {
  setGenericEvent,
  setSearchTracking
} from '../../../../../store/actions/dataLayer';
import { DEBOUNCE_TIMEOUT_LONG } from '../../../../../constants';

import { getBoatsConstants } from '../../../../../constants/boats';
import { PortalConfigContext } from '../../../../../config/portal';
import { scrollToTop } from '../../../../../utils/scrollTo';
import { Cookies } from 'react-cookie';

import './styles.css';
import { yieldToMain } from '../../../../../utils';
import { getMessages } from '../../../../../tppServices/translations/messages';

/* Loadable Facets */
const FilterCondition = loadable(() =>
  import('../../../Engines/components/Filter/FilterCondition')
);
const FilterPrice = loadable(() =>
  import('../../../components/Filter/FilterPrice')
);

const FilterPriceForm = loadable(() =>
  import('../../../components/Filter/FilterPriceForm')
);

const shouldCollapsibleBeOpen = (facetConfig, facetName) =>
  facetConfig.filter((facet) => facet.name === facetName)[0].initialState.open;

const RefineSearch = (props) => {
  const context = useContext(PortalConfigContext);
  const [state, setState] = useState({
    initialState: props.initialState || 'open',
    data: getDefaultParams(get(props, 'match.params', {})),
    wasRegionChangedByUser: false
  });

  const getCurrentState = () => {
    return getDefaultParams(get(props, 'match.params', {}));
  };

  const setCurrentState = () => {
    const params = getCurrentState();
    setState((prev) => ({
      ...prev,
      data: params
    }));
  };

  const prevUrlRef = useRef('');

  useEffect(() => {
    const currentUrl = get(props.match, 'url', '');
    if (currentUrl !== prevUrlRef.current) {
      setCurrentState();
    }
    prevUrlRef.current = currentUrl;
  }, [props.match?.url]);

  const debounceOnDataChange = useCallback(
    debounce((data) => {
      props.onDataChange(data);
    }),
    [props.onDataChange]
  );

  const handleDataChange = async(type, value) => {
    const regions = getRegions(get(props, 'facets'), context.country);
    let wasRegionChangedByUser = state.wasRegionChangedByUser;
    let location = {};

    if (type === 'region') {
      location.subdivision = '';
      wasRegionChangedByUser = value !== 'all';
    }

    if (type === 'subdivision' && value) {
      location.region = get(
        regions.find((region) => region.subdivision.includes(value)),
        'value',
        state.wasRegionChangedByUser ? state.data.region : ''
      );
    }

    if (type === 'country') {
      location.city = [];
    }

    const newState = {
      data: {
        ...state.data,
        [type]: value,
        ...location
      },
      wasRegionChangedByUser
    };

    setState(newState);

    debounceOnDataChange(newState.data);
  };

  const handleDataClear = async () => {
    await yieldToMain();
    props.setGenericEvent('site search', 'guided search', 'clear filters');
    const newState = {
      data: {
        ...getDefaultParams({}),
        sort: undefined,
        powerhp: undefined
      }
    };
    setState(newState);

    props.onDataChange(newState.data);
    scrollToTop();
  };

  const debouncedOnRangeChange = useCallback(
    debounce(
      (data) => props.onDataChange(data),
      DEBOUNCE_TIMEOUT_LONG
    ),
    [props.onDataChange]
  );

  const handleDebouncedDataChange = (type, value) => {
    const newState = {
      data: {
        ...state.data,
        [type]: value
      }
    };
    setState(newState);
    debouncedOnRangeChange(newState.data);
  };


  const facetAddedTracker = (facet) => {
    props.setSearchTracking(facet, 'single', 'guided search');
  };

  const facetRemovedTracker = (facet) => {
    props.setGenericEvent('site search', 'guided search', facet);
  };

  const isElectricFuelSelectd = () => {
    return get(state.data, 'fuel', '') === 'electric';
  };

    const messages = getMessages();
    const params = getCurrentState();
    const currentParams = getActiveParams(params);
    const tracking = {
      facetAdded: facetAddedTracker,
      facetRemoved: facetRemovedTracker
    };
    const {
      mobileParams,
      intl: { formatMessage: t }
    } = props;
    const { data } = state;
    const { year, price, powerhp = {}, makeModel } = data;
    const facets = get(props, 'facets', []);
    const makes = get(facets, 'make', []);
    const models = get(facets, 'makeModel', []);
    const categories = sortBy(get(facets, 'category', []), 'value');
    const fuelTypes = sortBy(
      get(facets, 'fuelType', []).filter(
        (fuelType) => isEmpty(params.powerhp) || fuelType.value !== 'electric'
      ),
      'value'
    );
    const countries = get(facets, 'country', []);
    const countryName = data.country ? t(messages.countries[data.country]) : '';
    const cities = getCities(facets, data.country);
    const regions = sortBy(getRegions(facets, context.country), 'value');
    const subdivisions = getSubdivisions(facets, context.country);
    const facetConfig = context.pages.searchResults.facets;
    const chosenRegion = state.wasRegionChangedByUser ? data.region : null;
    const isBranded = props.mode === MainContent.MODES.branded;
    const searchAlerts = get(
      context.supports,
      'engineSearchAlerts',
      false
    );

    return (
      <>
        {searchAlerts && (
          <SearchAlertsContainer
            cookies={props.cookies}
            match={props.match}
            eventCategory="above search filter"
          />
        )}
        <PillBox
          active={mobileParams || currentParams}
          params={getDefaultParams({})}
          handleDataChange={ handleDataChange }
          onClear={ handleDataClear }
          makeModel={models}
          searchType={getBoatsConstants().SEARCH_TYPES.engines}
        />
        <CollapsibleContent initialState="closed">
          <CollapsibleHeader priority={0}>
            {t(messages.category)}
          </CollapsibleHeader>
          <Collapsible>
            <FilterCategory
              selectedCategory={data.category}
              categories={categories}
              handleDataChange={handleDataChange}
              params={params}
              position={props.position}
              tracking={tracking}
            />
          </Collapsible>
        </CollapsibleContent>
        {facetConfig.filter(
          (facet) => facet.name === 'country' && facet.enabledForEngines
        ).length > 0 ? (
          <CollapsibleContent
            initialState={
              shouldCollapsibleBeOpen(facetConfig, 'country')
                ? 'open'
                : 'closed'
            }
          >
            <CollapsibleHeader priority={0}>
              {t(messages.country)}
            </CollapsibleHeader>
            <Collapsible>
              <div>
                <FilterCountry
                  selectedCountry={data.country}
                  countries={countries}
                  position={props.position}
                  handleDataChange={handleDataChange}
                  params={params}
                  tracking={tracking}
                />
              </div>
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        {facetConfig.filter(
          (facet) => facet.name === 'city' && facet.enabledForEngines
        ).length > 0 && cities.length > 0 ? (
          <CollapsibleContent
            initialState={
              shouldCollapsibleBeOpen(facetConfig, 'city') ? 'open' : 'closed'
            }
          >
            <CollapsibleHeader priority={0}>
              {t(messages.citiesInCountry, { country: countryName })}
            </CollapsibleHeader>
            <Collapsible>
              <div>
                <FilterCity
                  selectedCities={data.city}
                  selectedCountry={data.country}
                  cities={cities}
                  position={props.position}
                  handleDataChange={handleDataChange}
                  params={params}
                  tracking={tracking}
                />
              </div>
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        {facetConfig.filter(
          (facet) => facet.name === 'region' && facet.enabledForEngines
        ).length > 0 ? (
          <CollapsibleContent
            initialState={
              shouldCollapsibleBeOpen(facetConfig, 'region') ? 'open' : 'closed'
            }
          >
            <CollapsibleHeader priority={0}>
              {t(messages.region)}
            </CollapsibleHeader>
            <Collapsible>
              <FilterRegion
                region={data.region}
                regions={regions}
                position={props.position}
                handleDataChange={handleDataChange}
                params={params}
                tracking={tracking}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        {facetConfig.filter(
          (facet) => facet.name === 'subdivision' && facet.enabledForEngines
        ).length > 0 && subdivisions.length > 0 ? (
          <CollapsibleContent
            initialState={
              shouldCollapsibleBeOpen(facetConfig, 'subdivision')
                ? 'open'
                : 'closed'
            }
          >
            <CollapsibleHeader priority={0}>
              {t(messages.subdivision)}
            </CollapsibleHeader>
            <Collapsible>
              <FilterSubdivision
                subdivision={data.subdivision}
                chosenRegion={chosenRegion}
                subdivisions={subdivisions}
                position={props.position}
                handleDataChange={handleDataChange}
                params={params}
                tracking={tracking}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        {facetConfig.filter(
          (facet) => facet.name === 'condition' && facet.enabledForEngines
        ).length > 0 ? (
          <CollapsibleContent
            initialState={
              shouldCollapsibleBeOpen(facetConfig, 'condition')
                ? 'open'
                : 'closed'
            }
          >
            <CollapsibleHeader priority={0}>
              {t(messages.condition)}
            </CollapsibleHeader>
            <Collapsible>
              <FilterCondition
                position={props.position}
                condition={data.condition}
                handleDataChange={handleDataChange}
                tracking={tracking}
                id={`filter-condition-${props.position}`}
                params={params}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        {!isElectricFuelSelectd() ? (
          <CollapsibleContent initialState="open">
            <CollapsibleHeader priority={0}>
              {t(messages.powerhp)}
            </CollapsibleHeader>
            <Collapsible>
              <FilterHorsePower
                min={powerhp.min}
                max={powerhp.max}
                handleDataChange={handleDebouncedDataChange}
                tracking={tracking}
                id="filter-powerhp"
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        <CollapsibleContent initialState="open">
          <CollapsibleHeader priority={0}>
            {t(messages.price)}
          </CollapsibleHeader>
          <Collapsible>
          { props.position === 'desktop' ? (
            <FilterPriceForm
              min={price.min}
              max={price.max}
              handleDataChange={ handleDebouncedDataChange }
              tracking={tracking}
            />
          ) : (
            <FilterPrice
              min={price.min}
              max={price.max}
              handleDataChange={handleDebouncedDataChange}
              tracking={tracking}
              id="filter-price"
              position={props.position}
            />
          )}
          </Collapsible>
        </CollapsibleContent>
        <CollapsibleContent initialState="open">
          <CollapsibleHeader priority={0}>{t(messages.year)}</CollapsibleHeader>
          <Collapsible>
            { props.position === 'desktop' ? (
              <FilterYearForm
                min={ year.min }
                max={ year.max }
                handleDataChange={ handleDebouncedDataChange }
              />
            ) : (
            <FilterYear
              min={year.min}
              max={year.max}
              handleDataChange={handleDebouncedDataChange}
              tracking={tracking}
              position={props.position}
            />
          )}
          </Collapsible>
        </CollapsibleContent>
        <CollapsibleContent initialState="closed">
          <CollapsibleHeader priority={0}>{t(messages.make)}</CollapsibleHeader>
          <Collapsible>
            <FilterMake
              makes={makes}
              makeModels={makeModel}
              position={props.position}
              loading={includes(props.componentWorking, 'models')}
              handleDataChange={handleDataChange}
              params={params}
              tracking={tracking}
            />
          </Collapsible>
        </CollapsibleContent>
        <FilterModels
          makes={makes}
          makeModels={makeModel}
          models={models}
          position={props.position}
          loading={includes(props.componentWorking, 'models')}
          handleDataChange={handleDataChange}
          params={params}
          tracking={tracking}
        />
        <CollapsibleContent initialState="closed">
          <CollapsibleHeader priority={0}>
            {t(messages.fuelType)}
          </CollapsibleHeader>
          <Collapsible>
            <FilterFuelType
              selectedFuelType={data.fuel}
              fuelTypes={fuelTypes}
              handleDataChange={handleDataChange}
              params={params}
              position={props.position}
              tracking={tracking}
            />
          </Collapsible>
        </CollapsibleContent>
        {facetConfig.filter(
          (facet) => facet.name === 'forSale' && facet.enabledForEngines
        ).length > 0 && !isBranded ? (
          <CollapsibleContent
            initialState={
              shouldCollapsibleBeOpen(facetConfig, 'forSale')
                ? 'open'
                : 'closed'
            }
          >
            <CollapsibleHeader priority={0}>
              {t(messages.forSale)}
            </CollapsibleHeader>
            <Collapsible>
              <FilterForSaleBy
                position={props.position}
                selectedForSale={data.forSale}
                handleDataChange={handleDataChange}
                params={params}
                tracking={tracking}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}
      </>
    );
};

RefineSearch.propTypes = {
  match: PropTypes.object,
  componentWorking: PropTypes.arrayOf(PropTypes.string),
  facets: PropTypes.shape({
    class: PropTypes.arrayOf(
      PropTypes.shape({
        /** Class slug */
        value: PropTypes.string,
        count: PropTypes.number,
        /** Formatted class name */
        name: PropTypes.string,
        /** Formatted class type name */
        heading: PropTypes.string
      })
    ),
    country: PropTypes.arrayOf(
      PropTypes.shape({
        /** Country initials */
        value: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired
      })
    ),
    fuelType: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        count: PropTypes.number,
        /** Fuel type slug */
        urlId: PropTypes.string,
        /** Formatted fuel type name */
        name: PropTypes.string,
        heading: PropTypes.string
      })
    ),
    hullMaterial: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        count: PropTypes.number,
        /** Hull material slug */
        urlId: PropTypes.string,
        /** Formatted hull material name */
        name: PropTypes.string,
        heading: PropTypes.string
      })
    ),
    make: PropTypes.arrayOf(
      PropTypes.shape({
        /** Formatted make name */
        value: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired
      })
    ),
    makeModel: PropTypes.arrayOf(
      PropTypes.shape({
        /** Formatted make name */
        value: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        /** Models list for the make */
        model: PropTypes.arrayOf(
          PropTypes.shape({
            /** Model formatted name */
            value: PropTypes.string.isRequired,
            count: PropTypes.number.isRequired
          })
        )
      })
    ),
    subdivision: PropTypes.arrayOf(
      PropTypes.shape({
        /** State initials */
        value: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired
      })
    )
  }),
  initialState: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  mode: PropTypes.any,
  onDataChange: PropTypes.func.isRequired,
  position: PropTypes.string,
  setGenericEvent: PropTypes.func.isRequired,
  setSearchTracking: PropTypes.func.isRequired,
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  isWorking: PropTypes.bool,
  mobileParams: PropTypes.object,
};

const mapStateToProps = (state) => {
  let sortBy = get(state, 'app.sortBy');
  return { sortBy };
};

export default connect(mapStateToProps, (dispatch) =>
  bindActionCreators(
    {
      setGenericEvent,
      setSearchTracking
    },
    dispatch
  )
)(injectIntl(RefineSearch));
